@if (storageLoaded$ | async) {
  @if (useCollapsedMobileMenu$ | async) {
    <div
      class="mobile-menu-container"
      [class.custom-padding]="version > 1"
      [style.justify-content]="mobileMenuHeaderAlign"
    >
      <button
        type="button"
        class="burger"
        [class.open]="isMobileMenuOpen"
        (click)="toggleMobileMenuOpen()"
      >
        <div class="line"></div>
      </button>
      @if (mobileMenuBurgerLabel) {
        <span class="burger-label" [ngStyle]="fontStyles">{{ mobileMenuBurgerLabel }}</span>
      }
    </div>
  } @else {
    @let items = (visibleMenuItems$ | async) || [];
    <ng-container
      [ngTemplateOutlet]="items?.length ? menuItemsTpl : noItemsWarningTpl"
      [ngTemplateOutletContext]="{ $implicit: items }"
    />
  }
}

<ng-template #menuContentTpl>
  @let items = (visibleMenuItems$ | async) || [];
  <ng-container
    [ngTemplateOutlet]="items?.length ? menuItemsTpl : noItemsWarningTpl"
    [ngTemplateOutletContext]="{ $implicit: items }"
  />
</ng-template>

<ng-template #mobileMenuTpl>
  <div class="mobile-menu">
    <ng-container *ngTemplateOutlet="menuContentTpl" />
  </div>
</ng-template>

<ng-template #menuItemsTpl let-items>
  @let context =
    {
      currentPageId: (currentPageId$ | async) ?? '',
      visibleDropdownFor: (visibleDropdownFor$ | async) ?? undefined,
      useCollapsedMobileMenu: (useCollapsedMobileMenu$ | async) ?? false,
      isVertical: (isVertical$ | async) ?? false,
    };
  @let itemsHasIcons = hasIcons(items);

  <ul
    class="menu {{ context.isVertical ? 'vertical' : 'horizontal' }} {{ elementKeyRoot }} {{
      elementKey
    }}"
    [class.with-icons]="itemsHasIcons"
    [class.use-collapsed-mobile-menu]="context.useCollapsedMobileMenu"
    [ngClass]="menuClasses"
    #navMenu
  >
    @for (item of items; track item.id; let i = $index; let isLast = $last) {
      @let isSection = item.type === 'section';
      @let hasChildren = !!item.children?.length;

      @if (!isSection || hasChildren) {
        @let cyAttr = isSection ? 'NavMenu:section' : 'NavMenu:item';
        <li class="menu-item" [class.section]="isSection" [attr.data-cy]="cyAttr">
          @if (isSection) {
            @let sectionsHasIcons = hasIcons(items, 'section');
            <ng-container
              *ngTemplateOutlet="
                sectionItemTpl;
                context: {
                  $implicit: item,
                  context: context,
                  parentHasIcons: sectionsHasIcons,
                  isLast: isLast,
                }
              "
            />
          } @else {
            @let nextItem = items[i + 1];
            <ng-container
              *ngTemplateOutlet="
                menuItemTpl;
                context: {
                  $implicit: item,
                  context: context,
                  shouldAddDivider: getShouldAddDivider(item, nextItem),
                  parentHasIcons: itemsHasIcons,
                  isLast: isLast,
                }
              "
            />
          }
        </li>
      }
    }
  </ul>
</ng-template>

<ng-template
  #menuItemTpl
  let-item
  let-context="context"
  let-shouldAddDivider="shouldAddDivider"
  let-parentHasIcons="parentHasIcons"
>
  <button
    class="menu-button"
    #menuItemTrigger="matMenuTrigger"
    [ngStyle]="fontStyles"
    [class.has-icon]="menuHasIconClass(item, context, parentHasIcons)"
    [class.has-label]="item.props?.label"
    [class.active]="menuIsActiveClass(item, context)"
    [class.custom-padding]="version > 1"
    (click)="item.children?.length ? onMenuToggle($event, item) : onItemClick($event, item)"
    [matMenuTriggerFor]="getMatMenuTriggerTarget(item, context, matSubmenu)"
  >
    <span class="menu-label" data-cy="NavMenu:itemLabel">
      @if (menuShouldShowIcon(item, context, parentHasIcons)) {
        <mat-icon [svgIcon]="item.props?.icon || 'empty'" />
      }
      @if (item.props?.label) {
        <span class="menu-text">{{ item.props?.label }}</span>
      }
    </span>
    @if (item.children?.length) {
      <mat-icon
        svgIcon="{{
          isSameItem(context.visibleDropdownFor, item)
            ? 'icon_chevron_up_16'
            : 'icon_chevron_down_16'
        }}"
        data-cy="NavMenu:submenuIcon"
      />
    }
  </button>
  @if (
    (context.isVertical || context.useCollapsedMobileMenu) &&
    isSameItem(item, context.visibleDropdownFor)
  ) {
    <ng-container *ngTemplateOutlet="submenuTpl; context: { $implicit: item, context: context }" />
  }
  <mat-menu
    #matSubmenu="matMenu"
    class="{{ elementSubmenuKeyRoot }} {{ elementSubmenuKey }}"
    [hasBackdrop]="false"
  >
    <ng-template
      matMenuContent
      *ngTemplateOutlet="submenuTpl; context: { $implicit: item, context: context }"
    />
  </mat-menu>

  @if (shouldAddDivider) {
    <div class="divider">
      @if (showSectionDivider) {
        <hr class="divider-line" />
      }
    </div>
  }
</ng-template>

<ng-template #submenuTpl let-item let-context="context">
  <ul class="submenu {{ elementSubmenuKeyRoot }} {{ elementSubmenuKey }}" [ngClass]="menuClasses">
    @let hasIconsL2 = hasIcons(item.children);
    @for (subitem of item.children; track subitem.id) {
      <li class="submenu-item" data-cy="NavMenu:subItem">
        <button
          class="menu-button"
          (click)="onItemClick($event, subitem)"
          [ngStyle]="fontStyles"
          [class.has-icon]="submenuHasIconClass(item, subitem, context, hasIconsL2)"
          [class.active]="submenuIsActiveClass(subitem, context)"
          [class.custom-padding]="version > 1"
        >
          <span class="submenu-label" data-cy="NavMenu:subItemLabel">
            @if (submenuShouldShowIcon(item, subitem, context, hasIconsL2)) {
              <mat-icon [svgIcon]="subitem.props?.icon || 'empty'" />
            }
            @if (subitem.props?.label) {
              <span class="submenu-text">{{ subitem.props?.label }}</span>
            }
          </span>
        </button>
      </li>
    }
  </ul>
</ng-template>

<ng-template
  #sectionItemTpl
  let-item
  let-context="context"
  let-parentHasIcons="parentHasIcons"
  let-isLast="isLast"
>
  @let canCollapse = areSectionsCollapsible && item.children?.length;
  @let isExpanded = !canCollapse || expandedSections.has(item.id);
  @let itemsHasIcons = hasIcons(item.children);
  @let hasLabel = !!item.props?.label;

  @if (hasLabel || canCollapse) {
    <h3 class="section-header" [class.collapsible]="canCollapse" (click)="onSectionClick(item)">
      @if (hasLabel) {
        <span class="menu-label" data-cy="NavMenu:sectionLabel">
          <blaze-element-label
            [color]="props?.style?.sections?.label?.text?.color1"
            [fontFamily]="props?.style?.sections?.label?.fontFamily"
            [fontSize]="props?.style?.sections?.label?.fontSize"
            [fontStyle]="props?.style?.sections?.label?.fontStyle"
            [fontWeight]="props?.style?.sections?.label?.fontWeight"
            [icon]="
              menuShouldShowIcon(item, context, parentHasIcons)
                ? item.props?.icon || 'empty'
                : undefined
            "
            [iconColor]="props?.style?.sections?.label?.iconColor?.color1"
            [iconSize]="props?.style?.sections?.label?.iconSize"
            [lineHeight]="props?.style?.sections?.label?.lineHeight"
            minHeight="auto"
          >
            {{ item.props?.label }}
          </blaze-element-label>
        </span>
      }

      @if (canCollapse) {
        <mat-icon
          svgIcon="{{ isExpanded ? 'icon_chevron_up_16' : 'icon_chevron_down_16' }}"
          data-cy="NavMenu:sectionChevron"
        />
      }
    </h3>
  }
  @if (isExpanded && item.children?.length) {
    <ul
      class="menu vertical section-content"
      [class.with-icons]="itemsHasIcons"
      [class.use-collapsed-mobile-menu]="context.useCollapsedMobileMenu"
      [ngClass]="menuClasses"
      [ngStyle]="fontStyles"
    >
      @for (item of item.children; track item.id) {
        <li class="menu-item" data-cy="NavMenu:item">
          <ng-container
            *ngTemplateOutlet="
              menuItemTpl;
              context: { $implicit: item, context: context, parentHasIcons: itemsHasIcons }
            "
          />
        </li>
      }
    </ul>
  }
  @if (!isLast) {
    <div class="divider">
      @if (showSectionDivider) {
        <hr class="divider-line" />
      }
    </div>
  }
</ng-template>

<ng-template #noItemsWarningTpl>
  @if (isInEditor) {
    <div class="menu-notification {{ elementKeyRoot }} {{ elementKey }}">
      <div class="notification-content">
        <span>There are no defined menu items</span>
      </div>
    </div>
  }
</ng-template>
